import {
  Dispatch, SetStateAction, useCallback, useState,
} from 'react';
import {
  useHeroSnackbar,
  useTable,
  useQueryTableFetch,
  useTranslations,
  useTableLocalization,
  useFilterButton,
  useTablePreserverActivePageValidator,
  useTablePreserverDecoder,
  useTablePreserverEncoder,
  usePubSub,
  buildTablePreserverDecoderConfigItem,
  TablePreserverCommonInitialTableValues,
  IUseTableReturn,
  IUseTableResetTableMethodsReturn,
  IUseQueryTableFetchReturn,
  IUseTableLocalizationReturn,
  IUseFilterButtonReturn,
  TUseTablePreserverDecoderConfigs,
  TablePreserverDecoderConfigItemTypeEnum,
  TSetSearchParams,
} from '@uniqkey-frontend/shared-app';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import PubSubEventEnum from '../../../enums/PubSubEventEnum';
import ReactQueryKeyEnum from '../../../enums/ReactQueryKeyEnum';
import { IAuditLogsTableRow } from '../../../components/tables/AuditLogsTable';
import {
  IGetAuditLogsParams,
  TGetAuditLogsMethod,
} from '../../useAuditLogsAPI/interfaces';
import {
  IAuditLogsListFilterSubmitResult,
} from '../../../pages/AuditLogsPage/components/AuditLogsTab/components/AuditLogsListFilter';
import useAuditLogsAPI from '../../useAuditLogsAPI';

export const REACT_QUERY_AUDIT_LOGS_KEY = [ReactQueryKeyEnum.AuditLogs];

const AUDIT_LOGS_REACT_QUERY_CACHE_TIME = 30 * 1000;

export interface IUseAuditLogsTableParams {
  noDataMessageKey: string;
}

export interface IUseAuditLogsTableReturn extends
  Omit<
    IUseTableReturn<IAuditLogsTableRow>,
    'columnOrderBy' | 'columnOrderDirection' | 'selectedRows' | 'resetTableMethods'
  >,
  Pick<IUseTableResetTableMethodsReturn, 'resetActivePage'>,
  Omit<IUseQueryTableFetchReturn<TGetAuditLogsMethod>, 'data' | 'isFetchedAfterMount'>,
  IUseFilterButtonReturn
{
  auditLogs: IUseQueryTableFetchReturn<TGetAuditLogsMethod>['data'];
  selectedAuditLogs: IUseTableReturn<IAuditLogsTableRow>['selectedRows'];
  localization: IUseTableLocalizationReturn['localization'];
  filterValues: IAuditLogsListFilterSubmitResult;
  setFilterValues: Dispatch<SetStateAction<IAuditLogsListFilterSubmitResult>>;
}

interface ITablePreservationConfigs extends
  Omit<IGetAuditLogsParams,
    'page' | 'pageLength' | 'employeeAccountId' | 'beforeDate' | 'afterDate'
  >
{
  activePage: IGetAuditLogsParams['page'];
  perPage: IGetAuditLogsParams['pageLength'];
  employeeAccount: IAuditLogsListFilterSubmitResult['employeeAccount'];
  dateRange: IAuditLogsListFilterSubmitResult['dateRange'];
}

const PRESERVATION_CONFIGS: TUseTablePreserverDecoderConfigs<ITablePreservationConfigs> = {
  activePage: true,
  perPage: true,
  employeeAccount: true,
  dateRange: true,
  onlySupporterLogs: buildTablePreserverDecoderConfigItem(
    TablePreserverDecoderConfigItemTypeEnum.BOOLEAN,
  ),
};

const useAuditLogsTable = (
  params: IUseAuditLogsTableParams,
): IUseAuditLogsTableReturn => {
  const {
    noDataMessageKey,
  } = params;

  const queryClient = useQueryClient();
  const { t } = useTranslations();
  const { showError } = useHeroSnackbar();
  const { getAuditLogs } = useAuditLogsAPI();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleOnRequestError = useCallback(() => {
    showError({ text: t('common.somethingWentWrong') });
  }, [showError, t]);

  const {
    activePage: initialActivePage,
    perPage: initialPerPage,
    employeeAccount: initialEmployeeAccount,
    dateRange: initialDateRange,
    onlySupporterLogs: initialOnlySupporterLogs,
  } = useTablePreserverDecoder<ITablePreservationConfigs>(searchParams, PRESERVATION_CONFIGS);

  const [filterValues, setFilterValues] = useState<
    IAuditLogsListFilterSubmitResult
  >({
    employeeAccount: initialEmployeeAccount,
    dateRange: initialDateRange,
    onlySupporterLogs: initialOnlySupporterLogs,
  });

  const { isFilterActive, numberOfActiveFilters } = useFilterButton(filterValues);

  const {
    activePage,
    perPage,
    selectedRows: selectedAuditLogs,
    resetTableMethods,
    ...restTableProps
  } = useTable<IAuditLogsTableRow>({
    initialActivePage,
    initialPerPage,
  });

  const {
    resetTable,
    resetActivePage,
  } = resetTableMethods;

  const handleTableReset = useCallback(() => {
    resetTable();
    setFilterValues({
      employeeAccount: undefined,
      dateRange: TablePreserverCommonInitialTableValues.dateRange,
      onlySupporterLogs: undefined,
    });
  }, [resetTable]);
  usePubSub(PubSubEventEnum.RESET_TABLE, handleTableReset);

  const {
    data: auditLogs, isLoading, isFetchedAfterMount, total, resetQuery,
  } = useQueryTableFetch({
    queryKey: REACT_QUERY_AUDIT_LOGS_KEY,
    queryClient,
    request: getAuditLogs,
    params: {
      page: activePage,
      pageLength: perPage,
      employeeAccountId: filterValues.employeeAccount?.value,
      afterDate: filterValues.dateRange?.from ?? undefined,
      beforeDate: filterValues.dateRange?.to ?? undefined,
      onlySupporterLogs: filterValues.onlySupporterLogs,
    },
    onRequestError: handleOnRequestError,
    autoRefetch: true,
    useQueryOptions: {
      cacheTime: AUDIT_LOGS_REACT_QUERY_CACHE_TIME,
    },
  });

  const { localization } = useTableLocalization({
    isFilterActive,
    noDataMessageKey,
    isLoading,
  });

  useTablePreserverActivePageValidator({
    activePage, resetActivePage, perPage, total, isFetchedAfterMount,
  });

  useTablePreserverEncoder<ITablePreservationConfigs>(setSearchParams as TSetSearchParams, {
    activePage,
    perPage,
    employeeAccount: {
      value: filterValues.employeeAccount,
      parser: JSON.stringify,
    },
    dateRange: {
      value: filterValues.dateRange,
      parser: JSON.stringify,
    },
    onlySupporterLogs: filterValues.onlySupporterLogs,
  });

  return {
    auditLogs,
    isLoading,
    total,
    resetQuery,
    activePage,
    perPage,
    selectedAuditLogs,
    localization,
    filterValues,
    setFilterValues,
    isFilterActive,
    numberOfActiveFilters,
    resetActivePage,
    ...restTableProps,
  };
};

export default useAuditLogsTable;
